import type { TFunction } from 'i18next'
import { FlaskConical, History, Loader2, Plus, Search, Sparkles, Telescope } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { RelativeTime } from '@/components/custom-ui/RelativeTime'
import { PageHeading, PageShell } from '@/components/layout/PageShell'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { SearchInput } from '@/components/ui/search-input'
import { type SegmentedTab, SegmentedTabs } from '@/components/ui/segmented-tabs'
import { toast } from '@/components/ui/sonner'
import { useCreateHypothesis, useHypotheses, useTriggerHypothesisRun } from '@/features/hypotheses'
import { CreateEntityDialog } from '@/features/manual-graph/CreateEntityDialog'
import { DocumentFilter } from '@/features/search/DocumentFilter'
import { useBrowseExtracted } from '@/features/search/hooks/use-browse-extracted'
import { useDocumentFilter } from '@/features/search/hooks/use-document-filter'
import { type SearchMode, useSearchMode } from '@/features/search/hooks/use-search-mode'
import { useIsMobile } from '@/hooks/use-mobile'
import { useCaseContext } from '@/pages/cases/CaseContext'
import type { EntityHit } from '@/services/api/case-search'
import type { Hypothesis } from '@/types/hypothesis'
import { EntityDetailSheet } from './EntityDetailSheet'
import { GroupedBrowse } from './GroupedBrowse'

/** Runs shown before the first "load more", and how many each click reveals. */
const INITIAL_VISIBLE_RUNS = 6
const LOAD_MORE_STEP = 10

/** Both hero placeholders are longer than a phone-width field: smaller type, and
 * an ellipsis instead of a hard cut where they still do not fit. */
const PHONE_PLACEHOLDER = 'max-md:text-ellipsis max-md:placeholder:text-sm'

export default function SearchPage() {
  const { t } = useTranslation()
  const { caseId } = useCaseContext()
  // On the phone the heading scrolls away with the content instead of staying
  // pinned above the scroll area (PageShell's "full" layout pins it).
  const isMobile = useIsMobile()
  const heading = {
    icon: Telescope,
    title: t('nav.subviews.search'),
    summary: t('search.summary'),
  }
  // Per-case mode, held in a store so it survives leaving and returning to the
  // page (e.g. opening a run, then back) instead of resetting to Search.
  const [mode, setMode] = useSearchMode(caseId)
  // Research composer text, lifted so the "ask the agent" empty-state CTA in
  // Search mode can hand its query over when switching to Deep Research.
  const [researchQuery, setResearchQuery] = useState('')
  const [isCreatingEntity, setIsCreatingEntity] = useState(false)
  const navigate = useNavigate()
  // The Explore graph centres a node named by `?focus=` once it has it loaded.
  const showInGraph = (entityId: string) =>
    navigate(`/cases/${caseId}/investigate/explore-graph?focus=${encodeURIComponent(entityId)}`)

  const goResearch = (seed: string) => {
    setResearchQuery(seed)
    setMode('research')
  }
  // Fetched here only for the tab count (shown in both modes). The recent-runs
  // list re-reads the same cached query, so this is one request, not two.
  const { data: runs = [] } = useHypotheses(caseId)

  const tabs: SegmentedTab<SearchMode>[] = [
    { value: 'search', label: t('search.modes.search'), icon: <Search className="size-4" /> },
    {
      value: 'research',
      label: t('search.modes.research'),
      icon: <FlaskConical className="size-4" />,
      count: runs.length || undefined,
    },
  ]

  return (
    <PageShell layout="full" {...(isMobile ? {} : heading)}>
      <div className="flex flex-1 flex-col overflow-y-auto px-4 pb-8 md:px-6">
        {isMobile && (
          <div className="py-4">
            <PageHeading {...heading} />
          </div>
        )}
        <div className="w-full space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <SegmentedTabs<SearchMode> tabs={tabs} active={mode} onChange={setMode} />
            <Button variant="solid" onClick={() => setIsCreatingEntity(true)}>
              <Plus aria-hidden />
              {t('manualGraph.research.addEntity')}
            </Button>
          </div>
          {/* Each surface owns its own data hooks, so the inactive mode's
              requests (e.g. the browse query) never fire. */}
          {mode === 'search' ? (
            <SearchSurface caseId={caseId} onGoResearch={goResearch} />
          ) : (
            <ResearchSurface
              caseId={caseId}
              value={researchQuery}
              onValueChange={setResearchQuery}
            />
          )}
        </div>
      </div>
      <CreateEntityDialog
        caseId={caseId}
        open={isCreatingEntity}
        onOpenChange={setIsCreatingEntity}
        onShowExisting={candidate => showInGraph(candidate.id)}
        onShowInGraph={entity => showInGraph(entity.id)}
      />
    </PageShell>
  )
}

/** Search surface — live, debounced entity/document search. */
function SearchSurface({
  caseId,
  onGoResearch,
}: {
  caseId: string
  onGoResearch: (seed: string) => void
}) {
  const { t } = useTranslation()
  const [query, setQuery] = useState('')
  const [selectedEntity, setSelectedEntity] = useState<EntityHit | null>(null)
  const [document] = useDocumentFilter(caseId)
  const browse = useBrowseExtracted(caseId, query, document?.id)
  const term = query.trim()

  return (
    <>
      <div className="flex flex-col gap-2 md:flex-row">
        <SearchInput
          inputSize="lg"
          value={query}
          onValueChange={setQuery}
          placeholder={t('search.placeholder')}
          className={PHONE_PLACEHOLDER}
          containerClassName="min-w-0 flex-1"
          loading={browse.status === 'loading'}
        />
        <DocumentFilter caseId={caseId} className="h-12 md:w-64 lg:w-80" />
      </div>
      <GroupedBrowse
        caseId={caseId}
        results={browse.results}
        query={term}
        status={browse.status}
        onAskAgent={() => onGoResearch(term)}
        onSelectEntity={setSelectedEntity}
        documentFiltered={Boolean(document)}
      />
      <EntityDetailSheet
        caseId={caseId}
        hit={selectedEntity}
        onClose={() => setSelectedEntity(null)}
      />
    </>
  )
}

/** Deep Research surface — composer, suggestion chips, and recent runs. The
 * composer value is controlled by the parent so Search mode can seed it. */
function ResearchSurface({
  caseId,
  value,
  onValueChange,
}: {
  caseId: string
  value: string
  onValueChange: (v: string) => void
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const createH = useCreateHypothesis(caseId)
  const triggerRun = useTriggerHypothesisRun(caseId)
  const submitting = createH.isPending || triggerRun.isPending
  const canSubmit = value.trim().length > 0 && !submitting
  const suggestions = t('search.agent.suggestions', { returnObjects: true }) as string[]

  /** One-shot: create a hypothesis, navigate to its detail, kick off the run. */
  const runResearch = async () => {
    const trimmed = value.trim()
    if (!trimmed || submitting) return
    try {
      const h = await createH.mutateAsync({
        title: trimmed.slice(0, 80),
        caseDescription: trimmed,
        items: [],
        focusEntityIds: [],
      })
      navigate(`/cases/${caseId}/search/${h.id}`)
      triggerRun.mutate(h.id)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to start research')
    }
  }

  return (
    <div className="space-y-5">
      <div className="space-y-3">
        <form
          onSubmit={e => {
            e.preventDefault()
            runResearch()
          }}
        >
          <SearchInput
            inputSize="lg"
            value={value}
            onValueChange={onValueChange}
            placeholder={t('search.researchPlaceholder')}
            className={PHONE_PLACEHOLDER}
            action={
              <RunResearchButton
                submitting={submitting}
                disabled={!canSubmit}
                className="max-md:hidden"
              />
            }
          />
          {/* Phone: the button sits under the field so the question keeps the
              full width of the input. */}
          <RunResearchButton
            submitting={submitting}
            disabled={!canSubmit}
            className="mt-2 h-10 w-full md:hidden"
          />
        </form>

        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex shrink-0 items-center gap-1.5 pr-1 text-3xs font-semibold uppercase tracking-[0.12em] text-muted-foreground/80">
            <Sparkles className="size-3 text-primary/70" />
            {t('research.shelf.tryAsking')}
          </span>
          {suggestions.slice(0, 4).map(s => (
            <button
              key={s}
              type="button"
              onClick={() => onValueChange(s)}
              className="inline-flex max-w-full items-center truncate rounded-full border border-border/70 bg-card px-3 py-2 text-xs text-muted-foreground transition-colors hover:border-primary/40 hover:bg-primary/5 hover:text-foreground md:max-w-[28ch] md:py-1"
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      <RecentResearchList caseId={caseId} />
    </div>
  )
}

/** Submit button of the research composer — rendered inside the field on
 * desktop and under it on the phone. */
function RunResearchButton({
  submitting,
  disabled,
  className,
}: {
  submitting: boolean
  disabled: boolean
  className?: string
}) {
  const { t } = useTranslation()
  return (
    <Button type="submit" variant="solid" size="sm" disabled={disabled} className={className}>
      {submitting ? <Loader2 className="animate-spin" /> : <FlaskConical />}
      {t('search.runResearch')}
    </Button>
  )
}

/** Recent deep-research runs — the landing content of Deep Research mode. */
function RecentResearchList({ caseId }: { caseId: string }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data: runs = [] } = useHypotheses(caseId)
  const [visible, setVisible] = useState(INITIAL_VISIBLE_RUNS)

  if (runs.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border px-4 py-10 text-center text-sm text-muted-foreground">
        {t('search.recent.empty')}
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <span className="inline-flex items-center gap-1.5 text-3xs font-semibold uppercase tracking-[0.12em] text-muted-foreground/80">
        <History className="size-3" />
        {t('search.recent.title')}
      </span>
      <div className="space-y-2">
        {runs.slice(0, visible).map(run => (
          <ResearchRunRow
            key={run.id}
            run={run}
            onOpen={() => navigate(`/cases/${caseId}/search/${run.id}`)}
          />
        ))}
      </div>
      {runs.length > visible && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-full"
          onClick={() => setVisible(v => v + LOAD_MORE_STEP)}
        >
          {t('research.list.loadMore')}
        </Button>
      )}
    </div>
  )
}

function ResearchRunRow({ run, onOpen }: { run: Hypothesis; onOpen: () => void }) {
  const { t } = useTranslation()
  const badge = runBadge(run, t)

  return (
    <button
      type="button"
      onClick={onOpen}
      className="grid w-full grid-cols-[auto_minmax(0,1fr)] items-center gap-x-3 gap-y-1.5 rounded-lg border border-border bg-card px-4 py-3 text-left transition-colors hover:border-primary/40 hover:bg-muted/40 md:flex md:gap-3"
    >
      <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
        <FlaskConical className="size-4" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-foreground max-md:line-clamp-2 max-md:break-words md:truncate">
          {run.title}
        </p>
        {run.caseDescription && (
          <p className="truncate text-xs text-muted-foreground">{run.caseDescription}</p>
        )}
      </div>
      {/* Phone: status + date drop to a second line under the title. */}
      <div className="col-start-2 flex items-center gap-3 md:shrink-0">
        <Badge variant={badge.variant}>
          {badge.pulse && (
            <span className="size-1.5 animate-pulse rounded-full bg-current" aria-hidden />
          )}
          {badge.label}
        </Badge>
        <span className="text-2xs text-muted-foreground">
          <RelativeTime at={run.updatedAt} />
        </span>
      </div>
    </button>
  )
}

/** Deep research is a single run: it's either still running or completed. */
function runBadge(
  run: Hypothesis,
  t: TFunction
): { label: string; variant: 'info' | 'success'; pulse?: boolean } {
  const running = run.status === 'running' || run.status === 'draft'
  return running
    ? { label: t('search.runStatus.running'), variant: 'info', pulse: true }
    : { label: t('search.runStatus.completed'), variant: 'success' }
}
